/**
 * RUAAB Wishlist — native, app-free wishlist for the Hyper theme.
 * ---------------------------------------------------------------------------
 * ARCHITECTURE
 *
 * Storage is behind an ADAPTER so that a future server-backed store (custom app
 * + App Proxy writing customer metafields) can be dropped in WITHOUT touching
 * any UI code. Everything in this file talks to `WishlistStore` only.
 *
 *   WishlistStore.all()            -> Promise<Item[]>
 *   WishlistStore.has(id)          -> Promise<boolean>
 *   WishlistStore.add(item)        -> Promise<Item[]>
 *   WishlistStore.remove(id)       -> Promise<Item[]>
 *   WishlistStore.clear()          -> Promise<Item[]>
 *   WishlistStore.merge(items)     -> Promise<Item[]>   // union, never destructive
 *
 *   Item = { id: Number, handle: String, url: String, variantId: Number|null, addedAt: Number }
 *
 * To add cross-device sync later:
 *   1. Build a custom app + App Proxy endpoint (e.g. /apps/wishlist).
 *   2. Implement RemoteAdapter with the same 6 methods (fetch to that proxy).
 *   3. In WishlistStore.adapter getter, return RemoteAdapter when a customer is
 *      logged in (FoxTheme.wishlist.customerId), else LocalAdapter.
 *   4. On login, call WishlistStore.merge(LocalAdapter.read()) once — union merge,
 *      so a guest list is never overwritten by an empty account list.
 * Nothing else changes.
 *
 * EVENTS (published on FoxTheme.pubsub, mirroring the theme's compare feature;
 * also mirrored as a DOM CustomEvent on document for anything outside FoxTheme):
 *   wishlist:add    { items, item }
 *   wishlist:remove { items, id }
 *   wishlist:change { items }
 *
 * Custom elements:
 *   <wishlist-button>  heart toggle (product cards + PDP)
 *   <wishlist-count>   header bubble
 *   <wishlist-page>    hydrates the wishlist grid from storage
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = (window.FoxTheme && window.FoxTheme.wishlist) || {};
  var STORAGE_KEY = CONFIG.storageKey || 'hypertheme:wishlist-products';
  var STRINGS = CONFIG.strings || {};

  /* ---------------------------------------------------------------------- */
  /* Storage adapters                                                        */
  /* ---------------------------------------------------------------------- */

  var hasStorage = (function () {
    try {
      var k = '__ruaab_wl_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  })();

  /** In-memory fallback so the UI never throws in private mode / storage-blocked browsers. */
  var memory = [];

  var LocalAdapter = {
    read: function () {
      if (!hasStorage) return memory.slice();
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(function (i) { return i && i.id; }) : [];
      } catch (e) {
        return [];
      }
    },
    write: function (items) {
      if (!hasStorage) {
        memory = items.slice();
        return items;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* quota / private mode — degrade silently, list still works this session */
        memory = items.slice();
      }
      return items;
    }
  };

  var WishlistStore = {
    get adapter() {
      // Future: return RemoteAdapter when CONFIG.customerId is set AND the proxy exists.
      return LocalAdapter;
    },

    all: function () {
      return Promise.resolve(this.adapter.read());
    },

    has: function (id) {
      id = Number(id);
      return this.all().then(function (items) {
        return items.some(function (i) { return Number(i.id) === id; });
      });
    },

    add: function (item) {
      var self = this;
      return this.all().then(function (items) {
        if (items.some(function (i) { return Number(i.id) === Number(item.id); })) return items;
        var next = items.concat([
          {
            id: Number(item.id),
            handle: item.handle || '',
            url: item.url,
            variantId: item.variantId ? Number(item.variantId) : null,
            addedAt: Date.now()
          }
        ]);
        self.adapter.write(next);
        emit('wishlist:add', { items: next, item: item });
        emit('wishlist:change', { items: next });
        return next;
      });
    },

    remove: function (id) {
      var self = this;
      id = Number(id);
      return this.all().then(function (items) {
        var next = items.filter(function (i) { return Number(i.id) !== id; });
        self.adapter.write(next);
        emit('wishlist:remove', { items: next, id: id });
        emit('wishlist:change', { items: next });
        return next;
      });
    },

    clear: function () {
      var self = this;
      self.adapter.write([]);
      emit('wishlist:change', { items: [] });
      return Promise.resolve([]);
    },

    /** Union merge — used on login. Never drops entries from either side. */
    merge: function (incoming) {
      var self = this;
      return this.all().then(function (items) {
        var byId = {};
        items.concat(incoming || []).forEach(function (i) {
          if (!i || !i.id) return;
          var key = String(i.id);
          if (!byId[key] || (i.addedAt || 0) < (byId[key].addedAt || 0)) byId[key] = i;
        });
        var next = Object.keys(byId).map(function (k) { return byId[k]; });
        self.adapter.write(next);
        emit('wishlist:change', { items: next });
        return next;
      });
    }
  };

  function emit(name, detail) {
    if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
      window.FoxTheme.pubsub.publish(name, detail);
    }
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  /** Returns an unsubscribe fn — custom elements MUST call it on disconnect, or
   *  every AJAX re-render of a product grid leaks a listener + a detached node. */
  function on(name, cb) {
    var handler = function (e) { cb(e.detail || {}); };
    document.addEventListener(name, handler);
    return function () { document.removeEventListener(name, handler); };
  }

  // Expose for future adapters / integrations (Klaviyo, Meta, analytics).
  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.WishlistStore = WishlistStore;

  /* ---------------------------------------------------------------------- */
  /* <wishlist-button> — heart toggle                                        */
  /* ---------------------------------------------------------------------- */

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.productId = Number(this.dataset.productId);
          this.productUrl = this.dataset.productUrl;
          this.productHandle = this.dataset.productHandle || '';
          if (!this.button || !this.productId) return;

          this.onClick = this.onClick.bind(this);
          this.button.addEventListener('click', this.onClick);

          this.sync();
          this.unsub = on('wishlist:change', () => this.sync());
        }

        disconnectedCallback() {
          if (this.button) this.button.removeEventListener('click', this.onClick);
          if (this.unsub) this.unsub();
        }

        /** PDP: read the variant the customer has ACTUALLY selected, at click time. */
        get variantId() {
          if (this.dataset.variantId) return Number(this.dataset.variantId);
          const form = this.closest('form') || this.closest('.product-form, .product__info-wrapper, [id^="MainProduct"]');
          const input = form && form.querySelector('input[name="id"]');
          return input && input.value ? Number(input.value) : null;
        }

        onClick(event) {
          event.preventDefault();
          event.stopPropagation();
          if (this.busy) return;
          this.busy = true;

          WishlistStore.has(this.productId)
            .then((saved) => {
              if (saved) return WishlistStore.remove(this.productId);
              this.button.classList.add('is-bursting');
              setTimeout(() => this.button.classList.remove('is-bursting'), 600);
              return WishlistStore.add({
                id: this.productId,
                handle: this.productHandle,
                url: this.productUrl,
                variantId: this.variantId
              });
            })
            .then(() => {
              this.busy = false;
            });
        }

        sync() {
          WishlistStore.has(this.productId).then((saved) => {
            this.button.classList.toggle('is-saved', saved);
            this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
            const label = saved ? STRINGS.remove : STRINGS.add;
            this.button.setAttribute('aria-label', label || '');
            const sr = this.querySelector('.wishlist-button__label');
            if (sr) sr.textContent = label || '';
          });
        }
      }
    );
  }

  /* ---------------------------------------------------------------------- */
  /* <wishlist-count> — header bubble                                        */
  /* ---------------------------------------------------------------------- */

  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        connectedCallback() {
          this.render();
          on('wishlist:change', (d) => this.render(d.items));
        }

        render(items) {
          const apply = (list) => {
            const count = list.length;
            this.textContent = count > 99 ? '99+' : String(count);
            this.hidden = count === 0;
            this.setAttribute('aria-label', (STRINGS.count || 'Wishlist: %count% items').replace('%count%', count));
          };
          if (items) apply(items);
          else WishlistStore.all().then(apply);
        }
      }
    );
  }

  /* ---------------------------------------------------------------------- */
  /* <wishlist-page> — hydrates the grid                                     */
  /* ---------------------------------------------------------------------- */

  if (!customElements.get('wishlist-page')) {
    customElements.define(
      'wishlist-page',
      class WishlistPage extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wishlist-grid]');
          this.emptyState = this.querySelector('[data-wishlist-empty]');
          this.loading = this.querySelector('[data-wishlist-loading]');
          this.countEl = this.querySelector('[data-wishlist-page-count]');
          this.sectionName = this.dataset.itemSection || 'wishlist-item';

          this.addEventListener('click', this.onClick.bind(this));
          on('wishlist:change', () => this.render());
          this.render();
        }

        onClick(event) {
          const removeBtn = event.target.closest('[data-wishlist-remove]');
          if (removeBtn) {
            event.preventDefault();
            const card = removeBtn.closest('[data-wishlist-item]');
            const id = Number(removeBtn.dataset.wishlistRemove);
            if (card) {
              card.classList.add('is-removing');
              setTimeout(() => WishlistStore.remove(id), 220);
            } else {
              WishlistStore.remove(id);
            }
          }
        }

        render() {
          WishlistStore.all().then((items) => {
            // Newest first — the last thing they saved is the thing they're thinking about.
            const sorted = items.slice().sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

            if (this.countEl) {
              this.countEl.textContent = sorted.length
                ? (STRINGS.item_count || '%count% saved').replace('%count%', sorted.length)
                : '';
            }

            if (!sorted.length) {
              if (this.loading) this.loading.hidden = true;
              if (this.grid) {
                this.grid.innerHTML = '';
                this.grid.hidden = true;
              }
              if (this.emptyState) this.emptyState.hidden = false;
              return;
            }

            if (this.emptyState) this.emptyState.hidden = true;
            if (this.loading) this.loading.hidden = false;

            // Fetch each product's Liquid-rendered wishlist item via the Section
            // Rendering API. Same pattern the theme's compare bar uses.
            Promise.all(sorted.map((item) => this.fetchItem(item))).then((results) => {
              const html = results.map((r) => r.html).filter(Boolean).join('');
              if (this.loading) this.loading.hidden = true;
              if (this.grid) {
                this.grid.innerHTML = html;
                this.grid.hidden = false;
              }
              this.bindItems();

              // Prune ONLY on a hard 404 — the product is genuinely gone.
              // A network blip or 5xx must never silently delete a customer's saved
              // piece; on those we simply skip this item for this render pass.
              results.forEach((r) => {
                if (r.gone) WishlistStore.remove(r.id);
              });
            });
          });
        }

        /** Resolves to { id, html, gone } — `gone: true` ONLY on a 404. */
        fetchItem(item) {
          const url = item.url.split('?')[0];
          return fetch(`${url}?sections=${this.sectionName}`)
            .then((r) => {
              if (r.status === 404) return { id: item.id, html: '', gone: true };
              if (!r.ok) return { id: item.id, html: '', gone: false };
              return r.json().then((json) => {
                if (!json || !json[this.sectionName]) return { id: item.id, html: '', gone: false };
                const doc = new DOMParser().parseFromString(json[this.sectionName], 'text/html');
                const node = doc.querySelector('[data-wishlist-item]');
                if (!node) return { id: item.id, html: '', gone: false };
                if (item.variantId) node.setAttribute('data-preselect-variant', item.variantId);
                return { id: item.id, html: node.outerHTML, gone: false };
              });
            })
            .catch(() => ({ id: item.id, html: '', gone: false }));
        }

        /** Wire up the per-item variant selector (price + availability + hidden input). */
        bindItems() {
          this.grid.querySelectorAll('[data-wishlist-item]').forEach((card) => {
            const select = card.querySelector('[data-wishlist-variants]');
            if (!select) return;

            const preselect = card.getAttribute('data-preselect-variant');
            if (preselect) {
              const match = select.querySelector(`option[value="${preselect}"]`);
              if (match) select.value = preselect;
            }

            const update = () => {
              const opt = select.options[select.selectedIndex];
              if (!opt) return;
              const input = card.querySelector('input[name="id"]');
              const price = card.querySelector('[data-wishlist-price]');
              const submit = card.querySelector('button[name="add"]');
              const stock = card.querySelector('[data-wishlist-stock]');
              const available = opt.dataset.available === 'true';

              if (input) input.value = opt.value;
              if (price && opt.dataset.price) price.innerHTML = opt.dataset.price;
              if (submit) {
                submit.disabled = !available;
                const t = submit.querySelector('.btn__text') || submit;
                t.textContent = available ? STRINGS.add_to_cart || 'Add to cart' : STRINGS.sold_out || 'Sold out';
              }
              if (stock) {
                stock.textContent = available ? '' : STRINGS.sold_out || 'Sold out';
                stock.hidden = available;
              }
            };

            select.addEventListener('change', update);
            update();
          });
        }
      }
    );
  }
})();
